import { Component, Input, Output, EventEmitter, TemplateRef, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableColumn } from './models/table.models';
import { ColumnHeaderTruncatePipe } from '../pipes/column-header-truncate.pipe';

/*
 * GenericTableComponent est un composant Angular qui permet d'afficher un tableau générique
 * avec des colonnes configurables. Il prend en charge les colonnes fixes à gauche et à droite,
 * ainsi que la personnalisation des cellules et des en-têtes.
 * exemple d'utilisation :
 * dans le fichier html du composant parent :
 * <app-generic-table
 *  [columns]="columns"
 * [data]="data"
 * [cellPadding]="'0.5rem'"
 * ></app-generic-table>
 * 
 * Propriétés disponibles :
 * - columns: TableColumn[] - Configuration des colonnes
 * - data: any[] - Données du tableau
 * - tableCss: string - Classes CSS pour le tableau (défaut: "table table-hover table-bordered")
 * - cellPadding: string - Padding personnalisable pour les cellules (défaut: "0.75rem")
 * - autoWidth: boolean - Contrôle si le tableau doit s'adapter automatiquement à son contenu (défaut: true)
 * 
 * Configuration des colonnes :
 * - columnWidth: number (optionnel) - Largeur en pixels pour les colonnes non-sticky
 * - sticky.columnWidth: number (obligatoire si sticky activé) - Largeur en pixels pour les colonnes sticky
 *
 * <ng-template #nameTemplate let-row>
 *  <span> juste une test {{ row.name }} </span>
 *</ng-template>
 *
 *<ng-template #customHeader let-column>
 *  <div class="d-flex align-items-center" style="width: 100px">
 *    <i class="bi bi-info-circle me-2"></i>
 *    <span>{{ column.header }}</span>
 *  </div>
 *</ng-template>
 *
 * dans le fichier ts du composant parent :
 *  @ViewChild('customHeader', { static: true }) customHeader!: TemplateRef<any>;
 *  @ViewChild('nameTemplate', { static: true }) nameTemplate!: TemplateRef<any>;
 *   columns: TableColumn[] = [];
 *   rows: any[] = [];
 *
 * ngAfterViewInit(): void {
 *   //ngAfterViewInit pour assurer que le statusTemplate est bien défini avant d'être utilisé.
 *   this.columns = [
 *     {
 *       header: 'Name',
 *       field: 'name',
 *       headerTemplate: this.customHeader,
 *       cellTemplate: this.nameTemplate,
 *       sticky: {
 *         enabled: true,
 *         position: 'left',
 *         columnWidth: 100, // Largeur obligatoire pour sticky
 *       },
 *     },
 *     {
 *       header: 'Email',
 *       field: 'email',
 *       sticky: {
 *         enabled: true,
 *         position: 'left',
 *         columnWidth: 150, // Largeur obligatoire pour sticky
 *       },
 *     },
 *     {
 *       header: 'Status',
 *       field: 'status',
 *       columnWidth: 120, // Largeur optionnelle pour colonne non-sticky
 *     },
 *   ];
 *   this.rows = [
 *     {
 *       id: '1',
 *       name: 'John Doe',
 *       email: 'john.doe@example.com',
 *       status: 'Active',
 *       tv: 20,
 *        date: new Date(),
 *      },
 *      {
 *        id: '2',
 *        name: 'Jane Smith',
 *        email: 'jane.smith@example.com',
 *        status: 'Inactive',
 *        tv: 30,
 *        date: new Date(),
 *      },
 *    ];
 *  }
 *
 */

@Component({
  selector: 'app-generic-table',
  standalone: true,
  imports: [CommonModule, ColumnHeaderTruncatePipe],
  templateUrl: './generic-table.component.html',
  styleUrl: './generic-table.component.scss',
})
export class GenericTableComponent implements OnChanges {
  @Input() columns: TableColumn[] = []; // Configuration des colonnes
  @Input() data: any[] = []; // Données du tableau
  @Input() tableCss: string = "table table-hover table-bordered";
  @Input() cellPadding: string = "0.75rem"; // Padding personnalisable pour les cellules (par défaut: Bootstrap standard)
  @Input() autoWidth: boolean = true; // Contrôle si le tableau doit s'adapter automatiquement à son contenu
  @Input() columnFilterTemplate?: TemplateRef<any>; // Template pour le menu de filtre de colonne 
  @Input() enableCheckbox: boolean = false; // Activer les checkboxes de sélection
  @Input() selectedRows: Set<string> = new Set(); // Lignes sélectionnées (identifiées par leur ID)
  @Input() rowIdField: string = 'id'; // Champ utilisé comme identifiant unique pour chaque ligne
  @Output() selectionChange = new EventEmitter<Set<string>>(); // Événement émis lors du changement de sélection
  @Output() toggleAllSelection = new EventEmitter<boolean>(); // Événement émis lors de la sélection/désélection de toutes les lignes

  // Copie locale pour la détection de changement
  private localSelectedRows: Set<string> = new Set();
  
  constructor(private cdr: ChangeDetectorRef) {} 

  /*
   * La méthode getStickyLeftPosition calcule la position de la colonne fixe à gauche
   * en fonction de la largeur des colonnes précédentes qui sont également fixes à gauche.
   * Elle retourne la position en pixels ou null si la colonne n'est pas fixe à gauche.
   */
  getStickyLeftPosition(column: TableColumn): number | null {
    if (column.sticky && column.sticky.position === 'left') {
      const index = this.columns.findIndex((col) => col === column);
      // Ajouter la largeur de la colonne checkbox si elle est activée
      const checkboxWidth = this.enableCheckbox ? 40 : 0;
      return checkboxWidth + this.columns
        .slice(0, index)
        .filter((col) => col.sticky && col.sticky.position === 'left')
        .reduce((acc, col) => {
          const width = this.getColumnWidth(col);
          return acc + (width || 0);
        }, 0);
    }
    return null;
  }

  /*
   * La méthode getStickyRightPosition calcule la position de la colonne fixe à droite
   * en fonction de la largeur des colonnes suivantes qui sont également fixes à droite.
   * Elle retourne la position en pixels ou null si la colonne n'est pas fixe à droite.
   */
  getStickyRightPosition(column: TableColumn): number | null {
    if (column.sticky && column.sticky.position === 'right') {
      const index = this.columns.findIndex((col) => col === column);
      return this.columns
        .slice(index + 1)
        .filter((col) => col.sticky && col.sticky.position === 'right')
        .reduce((acc, col) => {
          const width = this.getColumnWidth(col);
          return acc + (width || 0);
        }, 0);
    }
    return null;
  }

  getColumnWidth(column: TableColumn): number | null {
    if (column.sticky?.enabled && !column.sticky.columnWidth) {
      throw new Error(
        `Column "${column.header}" is sticky but does not have a columnWidth defined.`
      );
    }
    // Pour les colonnes sticky, utiliser sticky.columnWidth
    if (column.sticky?.enabled) {
      return column.sticky.columnWidth;
    }
    // Pour les colonnes non-sticky, retourner columnWidth ou null pour largeur automatique
    return column.columnWidth || null;
  }

  trackByRow(index: number, row: any): any {
    return row && row.id ? row.id : index;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Synchroniser la copie locale avec l'input
    if (changes['selectedRows']) {
      // Toujours synchroniser, même si selectedRows est vide
      this.localSelectedRows = new Set(this.selectedRows || []);
      // Forcer la détection de changement pour mettre à jour les checkboxes
      this.cdr.detectChanges();
    }
  }

  getFormattedHeader(header: string): string {
    if (!header) return '';
    
    const maxFirstLine = 13;
    const maxSecondLine = 10;
    const maxTotal = 23;
    
    // Si le texte fait moins de 23 caractères, on peut l'afficher sur 2 lignes
    if (header.length <= maxTotal) {
      // Essayer de couper intelligemment sur 2 lignes
      if (header.length <= maxFirstLine) {
        // Une seule ligne suffit
        return header;
      }
      
      // Trouver le meilleur point de coupure pour la première ligne (max 13 caractères)
      let firstLine = header.substring(0, maxFirstLine);
      let secondLine = header.substring(maxFirstLine);
      
      // Chercher un espace pour couper proprement
      const lastSpaceIndex = firstLine.lastIndexOf(' ');
      if (lastSpaceIndex > 0 && lastSpaceIndex < maxFirstLine) {
        firstLine = header.substring(0, lastSpaceIndex);
        secondLine = header.substring(lastSpaceIndex + 1);
      }
      
      // Si la deuxième ligne dépasse 10 caractères, tronquer
      if (secondLine.length > maxSecondLine) {
        secondLine = secondLine.substring(0, maxSecondLine) + '...';
        return `${firstLine}<br>${secondLine}`;
      }
      
      return `${firstLine}<br>${secondLine}`;
    }
    
    // Si le texte dépasse 23 caractères, tronquer à 20 caractères et ajouter "..."
    return header.substring(0, 20) + '...';
  }

  isHeaderTruncated(header: string): boolean {
    if (!header) return false;
    const maxTotal = 23;
    return header.length > maxTotal;
  }

  getRowId(row: any): string | null {
    if (!row) return null;
    if (this.rowIdField.includes('.')) {
      // Gérer les champs imbriqués comme 'company.id'
      const fields = this.rowIdField.split('.');
      let value = row;
      for (const field of fields) {
        value = value?.[field];
        if (value === undefined || value === null) return null;
      }
      return String(value);
    }
    return String(row[this.rowIdField] || row.id || '');
  }

  isRowSelected(row: any): boolean {
    if (!this.enableCheckbox || !row) return false;
    const rowId = this.getRowId(row);
    if (!rowId) return false;
    // Utiliser la copie locale pour la détection de changement
    return this.localSelectedRows.has(rowId);
  }

  toggleRowSelection(row: any, event?: Event): void {
    if (!this.enableCheckbox || !row) return;
    
    // Empêcher la propagation de l'événement
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    const rowId = this.getRowId(row);
    if (!rowId) {
      console.warn('toggleRowSelection: rowId is null', row, { rowIdField: this.rowIdField });
      return;
    }
    
    // Créer une copie du Set pour pouvoir le modifier
    const newSelectedRows = new Set(this.localSelectedRows);
    const wasSelected = newSelectedRows.has(rowId);
    
    if (wasSelected) {
      newSelectedRows.delete(rowId);
    } else {
      newSelectedRows.add(rowId);
    }
    
    // Mettre à jour la copie locale AVANT d'émettre
    this.localSelectedRows = new Set(newSelectedRows);
    
    // Émettre la nouvelle valeur
    this.selectionChange.emit(newSelectedRows);
    
    // Forcer la détection de changement après la mise à jour
    this.cdr.detectChanges();
  }

  isAllSelected(): boolean {
    if (!this.enableCheckbox || this.data.length === 0) return false;
    return this.data.length > 0 && this.data.every(row => {
      const rowId = this.getRowId(row);
      return rowId ? this.localSelectedRows.has(rowId) : false;
    });
  }

  isIndeterminate(): boolean {
    if (!this.enableCheckbox || this.data.length === 0) return false;
    const selectedCount = this.data.filter(row => {
      const rowId = this.getRowId(row);
      return rowId ? this.localSelectedRows.has(rowId) : false;
    }).length;
    return selectedCount > 0 && selectedCount < this.data.length;
  }

  toggleAllRows(event: Event): void {
    if (!this.enableCheckbox) return;
    const isChecked = (event.target as HTMLInputElement).checked;
    
    // Créer une nouvelle copie du Set
    const newSelectedRows = new Set<string>();
    
    if (isChecked) {
      // Sélectionner toutes les lignes visibles
      this.data.forEach((row) => {
        const rowId = this.getRowId(row);
        if (rowId) {
          newSelectedRows.add(rowId);
        }
      });
    }
    
    // Mettre à jour la copie locale
    this.localSelectedRows = newSelectedRows;
    
    // Émettre la nouvelle valeur
    this.selectionChange.emit(newSelectedRows);
    this.toggleAllSelection.emit(isChecked);
    
    // Forcer la détection de changement après l'émission
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }
}
